import type { Vector2 } from './contracts';

export interface RoomDefinition {
  readonly id: string;
  readonly name: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly powerBus: 'A' | 'B' | null;
  readonly pressure: 'pressurized' | 'vacuum';
  readonly tint: number;
}

export interface PassageDefinition {
  readonly id: string;
  readonly from: string;
  readonly to: string;
  readonly position: Vector2;
  readonly orientation: 'vertical' | 'horizontal';
  readonly initiallyOpen: boolean;
  readonly cuttable: boolean;
  readonly cutPointId?: string;
}

export interface SalvageRuntimeDefinition {
  readonly id: string;
  readonly label: string;
  readonly roomId: string;
  readonly position: Vector2;
  readonly mass: number;
  readonly radius: number;
  readonly required: boolean;
  readonly color: number;
}

export interface AuthoredBreakPoint {
  readonly id: string;
  readonly cutPointId: string;
  readonly position: Vector2;
  readonly kind: 'passage' | 'attachment';
}

export const WORLD_WIDTH = 2080;
export const WORLD_HEIGHT = 860;
export const ROOM_WIDTH = 440;
export const ROOM_HEIGHT = 270;

export const WRECK_ROOMS: readonly RoomDefinition[] = [
  {
    id: 'docking-throat',
    name: 'Docking throat',
    x: 80,
    y: 470,
    width: 360,
    height: 150,
    powerBus: null,
    pressure: 'vacuum',
    tint: 0x0c2631,
  },
  {
    id: 'maintenance-access',
    name: 'Maintenance access',
    x: 470,
    y: 470,
    width: ROOM_WIDTH,
    height: 150,
    powerBus: 'A',
    pressure: 'vacuum',
    tint: 0x0b202b,
  },
  {
    id: 'crew-corridor',
    name: 'Crew corridor',
    x: 940,
    y: 470,
    width: ROOM_WIDTH,
    height: 150,
    powerBus: 'A',
    pressure: 'vacuum',
    tint: 0x102732,
  },
  {
    id: 'power-control',
    name: 'Power control',
    x: 1410,
    y: 470,
    width: 400,
    height: 150,
    powerBus: 'A',
    pressure: 'pressurized',
    tint: 0x152b31,
  },
  {
    id: 'cargo-blister',
    name: 'Cargo blister',
    x: 1410,
    y: 300,
    width: 400,
    height: 150,
    powerBus: 'B',
    pressure: 'pressurized',
    tint: 0x132530,
  },
  {
    id: 'navigation-chamber',
    name: 'Navigation chamber',
    x: 940,
    y: 300,
    width: ROOM_WIDTH,
    height: 150,
    powerBus: 'B',
    pressure: 'pressurized',
    tint: 0x102936,
  },
  {
    id: 'observation-dome',
    name: 'Observation dome',
    x: 470,
    y: 300,
    width: ROOM_WIDTH,
    height: 150,
    powerBus: 'B',
    pressure: 'vacuum',
    tint: 0x0a2532,
  },
  {
    id: 'service-shaft',
    name: 'Service shaft',
    x: 80,
    y: 300,
    width: 360,
    height: 150,
    powerBus: 'A',
    pressure: 'vacuum',
    tint: 0x0b1d26,
  },
] as const;

export const WRECK_PASSAGES: readonly PassageDefinition[] = [
  {
    id: 'tutorial-hatch',
    from: 'docking-throat',
    to: 'maintenance-access',
    position: { x: 455, y: 545 },
    orientation: 'vertical',
    initiallyOpen: false,
    cuttable: true,
    cutPointId: 'tutorial-hatch-seam',
  },
  {
    id: 'maintenance-corridor',
    from: 'maintenance-access',
    to: 'crew-corridor',
    position: { x: 925, y: 545 },
    orientation: 'vertical',
    initiallyOpen: true,
    cuttable: false,
  },
  {
    id: 'corridor-power',
    from: 'crew-corridor',
    to: 'power-control',
    position: { x: 1395, y: 545 },
    orientation: 'vertical',
    initiallyOpen: true,
    cuttable: false,
  },
  {
    id: 'power-cargo',
    from: 'power-control',
    to: 'cargo-blister',
    position: { x: 1610, y: 460 },
    orientation: 'horizontal',
    initiallyOpen: true,
    cuttable: false,
  },
  {
    id: 'cargo-navigation',
    from: 'cargo-blister',
    to: 'navigation-chamber',
    position: { x: 1395, y: 375 },
    orientation: 'vertical',
    initiallyOpen: false,
    cuttable: false,
  },
  {
    id: 'navigation-observation',
    from: 'navigation-chamber',
    to: 'observation-dome',
    position: { x: 925, y: 375 },
    orientation: 'vertical',
    initiallyOpen: true,
    cuttable: false,
  },
  {
    id: 'observation-service',
    from: 'observation-dome',
    to: 'service-shaft',
    position: { x: 455, y: 375 },
    orientation: 'vertical',
    initiallyOpen: false,
    cuttable: true,
    cutPointId: 'service-seam',
  },
  {
    id: 'service-dock',
    from: 'service-shaft',
    to: 'docking-throat',
    position: { x: 250, y: 460 },
    orientation: 'horizontal',
    initiallyOpen: false,
    cuttable: false,
  },
  {
    id: 'corridor-navigation-shortcut',
    from: 'crew-corridor',
    to: 'navigation-chamber',
    position: { x: 1160, y: 460 },
    orientation: 'horizontal',
    initiallyOpen: false,
    cuttable: true,
    cutPointId: 'shortcut-seam',
  },
] as const;

export const SALVAGE_OBJECTS: readonly SalvageRuntimeDefinition[] = [
  {
    id: 'navigation-core',
    label: 'Navigation core',
    roomId: 'navigation-chamber',
    position: { x: 1160, y: 375 },
    mass: 12,
    radius: 24,
    required: true,
    color: 0x4fd8ff,
  },
  {
    id: 'data-prism',
    label: 'Data prism',
    roomId: 'observation-dome',
    position: { x: 690, y: 375 },
    mass: 4,
    radius: 16,
    required: false,
    color: 0xb8f2ff,
  },
  {
    id: 'reactor-plate',
    label: 'Reactor plate',
    roomId: 'cargo-blister',
    position: { x: 1610, y: 375 },
    mass: 28,
    radius: 31,
    required: false,
    color: 0xffb45a,
  },
  {
    id: 'medical-case',
    label: 'Medical case',
    roomId: 'crew-corridor',
    position: { x: 1160, y: 545 },
    mass: 7,
    radius: 19,
    required: false,
    color: 0x75d49a,
  },
  {
    id: 'tool-cassette',
    label: 'Tool cassette',
    roomId: 'maintenance-access',
    position: { x: 650, y: 545 },
    mass: 5,
    radius: 17,
    required: false,
    color: 0xffd36a,
  },
  {
    id: 'flight-recorder',
    label: 'Flight recorder',
    roomId: 'service-shaft',
    position: { x: 250, y: 375 },
    mass: 9,
    radius: 20,
    required: false,
    color: 0xff6170,
  },
] as const;

export const AUTHORED_BREAK_POINTS: readonly AuthoredBreakPoint[] = [
  ...WRECK_PASSAGES.flatMap((passage) =>
    passage.cutPointId
      ? [
          {
            id: passage.id,
            cutPointId: passage.cutPointId,
            position: passage.position,
            kind: 'passage' as const,
          },
        ]
      : [],
  ),
  {
    id: 'core-bolt-port',
    cutPointId: 'core-bolt-port',
    position: { x: 1140, y: 360 },
    kind: 'attachment',
  },
  {
    id: 'core-bolt-starboard',
    cutPointId: 'core-bolt-starboard',
    position: { x: 1182, y: 390 },
    kind: 'attachment',
  },
  {
    id: 'prism-catch',
    cutPointId: 'prism-catch',
    position: { x: 690, y: 355 },
    kind: 'attachment',
  },
  {
    id: 'plate-clamp',
    cutPointId: 'plate-clamp',
    position: { x: 1610, y: 350 },
    kind: 'attachment',
  },
  {
    id: 'recorder-panel',
    cutPointId: 'recorder-panel',
    position: { x: 250, y: 350 },
    kind: 'attachment',
  },
] as const;

export function roomAt(position: Vector2): RoomDefinition | null {
  return (
    WRECK_ROOMS.find(
      (room) =>
        position.x >= room.x &&
        position.x <= room.x + room.width &&
        position.y >= room.y &&
        position.y <= room.y + room.height,
    ) ?? null
  );
}
